import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const PageContainer = styled.div`
  padding: 40px 0;
`;

const PageHeader = styled.h1`
  font-size: 2rem;
  margin-bottom: 20px;
  position: sticky;
  top: 180px;
  background-color: #000;
  z-index: 100;
  padding: 15px 0;
  margin-top: -15px;

  @media (max-width: 768px) {
    top: 140px;
  }
`;

const PageDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 30px;
  max-width: 800px;
  color: #ddd;
`;

const FormContainer = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 30px;
  max-width: 800px;
  margin-bottom: 40px;
`;

const FormTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 25px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #2c7d46;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #2c7d46;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #2c7d46;
  }
`;

const SubmitButton = styled.button`
  background: #2c7d46;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  font-weight: bold;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: #36964f;
  }
  
  &:disabled {
    background: #666;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  padding: 20px;
  background-color: rgba(44, 125, 70, 0.2);
  border: 1px solid #2c7d46;
  color: #fff;
  border-radius: 4px;
  margin-top: 20px;
  margin-bottom: 20px;
`;

const FileUploadContainer = styled.div`
  margin-bottom: 25px;
`;

const FileInputLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
`;

const FileInputButton = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  padding: 10px 15px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 10px;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  svg {
    margin-right: 8px;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ImagePreviewContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 15px;
  margin-top: 15px;
`;

const ImagePreview = styled.div`
  position: relative;
  height: 120px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.7);
  border: none;
  color: white;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }
`;

const FileInputHelper = styled.p`
  font-size: 0.85rem;
  color: #aaa;
  margin-top: 5px;
`;

const CommissionProcess = styled.div`
  margin-top: 50px;
`;

const ProcessTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 20px;
`;

const ProcessSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 30px;
  margin-top: 20px;
`;

const Step = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 20px;
  position: relative;
`;

const StepNumber = styled.div`
  position: absolute;
  top: -15px;
  left: 20px;
  width: 40px;
  height: 40px;
  background: #2c7d46;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.2rem;
`;

const StepTitle = styled.h3`
  font-size: 1.2rem;
  margin-top: 15px;
  margin-bottom: 10px;
`;

const StepDescription = styled.p`
  font-size: 0.9rem;
  line-height: 1.5;
  color: #ddd;
`;

function Commission() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    medium: '',
    size: '',
    description: '',
    budget: '',
    timeline: ''
  });

  const [images, setImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const MAX_IMAGES = 5;
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes

    if (files.length > 0) {
      // Limit to max 5 images total
      const remainingSlots = MAX_IMAGES - images.length;

      if (remainingSlots <= 0) {
        alert('You can upload a maximum of 5 images. Please remove some images before adding more.');
        e.target.value = null;
        return;
      }

      // Check file sizes and filter out files that are too large
      const validFiles = [];
      const invalidFiles = [];

      for (let i = 0; i < Math.min(files.length, remainingSlots); i++) {
        const file = files[i];
        if (file.size <= MAX_FILE_SIZE) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      }

      // Notify user about files that are too large
      if (invalidFiles.length > 0) {
        alert(`The following files exceed the 5MB size limit and were not added:\n${invalidFiles.join('\n')}`);
      }

      if (validFiles.length > 0) {
        const newImages = validFiles.map(file => ({
          file,
          preview: URL.createObjectURL(file),
          name: file.name,
          size: formatFileSize(file.size)
        }));

        setImages(prev => [...prev, ...newImages]);
      }
    }

    // Clear input so the same file can be selected again if needed
    e.target.value = null;
  };

  // Helper function to format file sizes
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' bytes';
    else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const removeImage = (index) => {
    setImages(prev => {
      const newImages = [...prev];
      // Revoke the object URL to avoid memory leaks
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      // Cleanup function to run when component unmounts
      images.forEach(image => {
        URL.revokeObjectURL(image.preview);
      });
    };
  }, [images]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Prepare data for submission (including images)
    const submissionData = {
      ...formData,
      images: images.map(img => ({
        name: img.name,
        file: img.file
      }))
    };

    // Simulate form submission
    setTimeout(() => {
      console.log('Commission request submitted:', submissionData);
      setIsSubmitting(false);
      setIsSubmitted(true);

      // Clean up image previews to avoid memory leaks
      images.forEach(image => URL.revokeObjectURL(image.preview));

      // Reset form after submission
      setFormData({
        name: '',
        email: '',
        phone: '',
        medium: '',
        size: '',
        description: '',
        budget: '',
        timeline: ''
      });
      setImages([]);
    }, 1500);
  };
  
  return (
    <PageContainer>
      <PageHeader>Commission a Piece</PageHeader>
      
      <PageDescription>
        I'm delighted to create a custom piece of art specifically for you. 
        Commissioned works are a personal journey we take together, 
        resulting in a unique artwork that perfectly matches your vision 
        and space. Please fill out the form below to begin the conversation.
      </PageDescription>
      
      <FormContainer>
        <FormTitle>Commission Request Form</FormTitle>
        
        {isSubmitted ? (
          <SuccessMessage>
            <h3>Thank you for your commission request!</h3>
            <p>I've received your information and will contact you within 2-3 business days to discuss your project in more detail.</p>
            <p>Your reference images have been received and will be reviewed as part of your request.</p>
          </SuccessMessage>
        ) : (
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="name">Your Name *</Label>
              <Input 
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="email">Email Address *</Label>
              <Input 
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="phone">Phone Number</Label>
              <Input 
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="medium">Preferred Medium</Label>
              <Select
                id="medium"
                name="medium"
                value={formData.medium}
                onChange={handleChange}
              >
                <option value="">Select a medium</option>
                <option value="encaustic">Encaustic</option>
                <option value="acrylic-canvas">Acrylic on Canvas</option>
                <option value="acrylic-paper">Acrylic on Paper</option>
                <option value="acrylic-pen-canvas">Acrylic Pen on Canvas</option>
                <option value="acrylic-pen-cloth">Acrylic Pen on Cloth</option>
                <option value="other">Other (specify in description)</option>
              </Select>
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="size">Desired Size</Label>
              <Select
                id="size"
                name="size"
                value={formData.size}
                onChange={handleChange}
              >
                <option value="">Select a size</option>
                <option value="small">Small (up to 24" x 24")</option>
                <option value="medium">Medium (24" x 36" to 36" x 48")</option>
                <option value="large">Large (larger than 36" x 48")</option>
                <option value="custom">Custom (specify in description)</option>
              </Select>
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="description">Project Description *</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Please describe your vision for this commission. Include any specific colors, themes, or inspiration you have in mind."
                required
              />
            </FormGroup>

            <FileUploadContainer>
              <FileInputLabel>Reference Images</FileInputLabel>
              <FileInputButton onClick={handleFileButtonClick}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                Add Reference Images
              </FileInputButton>
              <HiddenFileInput
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                multiple
              />
              <FileInputHelper>
                Upload photos of your space, color inspiration.
                (Max 5 images, JPG, PNG or GIF format, max 5MB per image)
              </FileInputHelper>

              {images.length > 0 && (
                <ImagePreviewContainer>
                  {images.map((image, index) => (
                    <ImagePreview key={index}>
                      <PreviewImage src={image.preview} alt={`Preview ${index + 1}`} />
                      <RemoveImageButton
                        type="button"
                        onClick={() => removeImage(index)}
                        aria-label="Remove image"
                      >
                        ×
                      </RemoveImageButton>
                      <div style={{ position: 'absolute', bottom: '5px', left: '5px', fontSize: '0.7rem', background: 'rgba(0,0,0,0.6)', padding: '2px 5px', borderRadius: '2px' }}>
                        {image.size}
                      </div>
                    </ImagePreview>
                  ))}
                </ImagePreviewContainer>
              )}
            </FileUploadContainer>

            <FormGroup>
              <Label htmlFor="budget">Budget Range</Label>
              <Select
                id="budget"
                name="budget"
                value={formData.budget}
                onChange={handleChange}
              >
                <option value="">Select a budget range</option>
                <option value="500-1000">$500 - $1,000</option>
                <option value="1000-2500">$1,000 - $2,500</option>
                <option value="2500-5000">$2,500 - $5,000</option>
                <option value="5000+">$5,000+</option>
              </Select>
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="timeline">Desired Timeline</Label>
              <Select
                id="timeline"
                name="timeline"
                value={formData.timeline}
                onChange={handleChange}
              >
                <option value="">Select a timeline</option>
                <option value="1-2months">1-2 months</option>
                <option value="3-4months">3-4 months</option>
                <option value="5-6months">5-6 months</option>
                <option value="flexible">Flexible</option>
              </Select>
            </FormGroup>
            
            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Commission Request'}
            </SubmitButton>
          </form>
        )}
      </FormContainer>
      
      <CommissionProcess>
        <ProcessTitle>The Commission Process</ProcessTitle>
        
        <ProcessSteps>
          <Step>
            <StepNumber>1</StepNumber>
            <StepTitle>Initial Consultation</StepTitle>
            <StepDescription>
              After receiving your request, we'll schedule a call or video chat to discuss your vision, preferences, and requirements in detail.
            </StepDescription>
          </Step>
          
          <Step>
            <StepNumber>2</StepNumber>
            <StepTitle>Proposal & Agreement</StepTitle>
            <StepDescription>
              I'll provide a detailed proposal including concept sketches, timeline, and pricing. Once approved, payment is due in full.
            </StepDescription>
          </Step>
          
          <Step>
            <StepNumber>3</StepNumber>
            <StepTitle>Delivery</StepTitle>
            <StepDescription>
              When completed, your commissioned artwork will be carefully packaged and delivered to you.
            </StepDescription>
          </Step>
        </ProcessSteps>
      </CommissionProcess>
    </PageContainer>
  );
}

export default Commission;